import {Typography, Box, Button} from "@mui/material"
import {styled} from "@mui/material/styles"
import {ReactNode} from "react"

import {colors} from "@indebted/theme/colors"

function SettlementOffer({
	HeaderDescription,
	HeaderDiscount,
	BalanceToPay,
	OutstandingBalanceWithDiscountValue,
	SaveDiscountValue,
	ButtonLabel,
	Footnote,
	ShowFootnote,
	TermsOfDiscountOffer,
	onClick,
}: {
	HeaderDescription: string
	HeaderDiscount: string
	BalanceToPay: string
	OutstandingBalanceWithDiscountValue: string
	SaveDiscountValue: string
	ButtonLabel: string
	Footnote?: string
	ShowFootnote: boolean
	TermsOfDiscountOffer: ReactNode
	onClick: () => void
}) {
	return (
		<DiscountBox>
			<DiscountHeader>
				<Typography
					variant="body2"
					sx={{fontSize: "0.875rem", textAlign: "start", fontWeight: 500, color: "inherit", opacity: 0.8}}
				>
					{HeaderDescription}
				</Typography>
				<Typography
					variant="h5"
					fontWeight={700}
					sx={{fontSize: "2.25rem", fontWeight: 700, lineHeight: "2.75rem", textAlign: "start"}}
				>
					{HeaderDiscount}
				</Typography>
			</DiscountHeader>

			<DiscountBody>
				<DiscountBodyContent>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{fontSize: "1rem", textAlign: "start", fontWeight: 500, lineHeight: "1.5rem"}}
					>
						{BalanceToPay}
					</Typography>
					<Box>
						<Typography
							variant="h4"
							fontWeight={700}
							color="secondary"
							sx={{
								fontSize: "2.25rem",
								textAlign: "start",
								display: "inline-block",
								marginRight: "0.25rem",
							}}
						>
							{OutstandingBalanceWithDiscountValue}
						</Typography>
						<Typography
							component="span"
							variant="body1"
							color={colors.endlessGalaxy["400"]}
							sx={{
								fontSize: "1rem",
								textAlign: "start",
								fontWeight: 700,
								display: "inline-block",
								lineHeight: "1.5rem",
							}}
						>
							{SaveDiscountValue}
						</Typography>
					</Box>
					<Typography variant="body2" color="secondary" sx={{fontSize: "0.875rem", fontWeight: 400}}>
						{TermsOfDiscountOffer}
					</Typography>
				</DiscountBodyContent>
				<Button fullWidth variant="contained" onClick={onClick}>
					{ButtonLabel}
				</Button>
				<FootnoteBox showFootnote={ShowFootnote}>
					<Typography
						variant="body2"
						color={colors.endlessGalaxy["700"]}
						sx={{fontSize: "0.75rem", textAlign: "center", fontWeight: 400, paddingTop: "0.5rem"}}
					>
						{Footnote}
					</Typography>
				</FootnoteBox>
			</DiscountBody>
		</DiscountBox>
	)
}

const DiscountBox = styled(Box)(({theme}) => ({
	display: "flex",
	flexDirection: "column",
	background: theme.palette.common.white,
	borderRadius: 16,
	overflow: "hidden",
	padding: 0,
	boxShadow: "0 4px 14px 2px rgba(54, 59, 100, 0.10)",
	width: "100%",
	height: "100%",
}))

const DiscountHeader = styled(Box)(({theme}) => ({
	background: colors.endlessGalaxy["900"],
	color: theme.palette.common.white,
	textAlign: "center",
	/* top | left and right | bottom */
	padding: "1rem 1rem 0.25rem",
	[theme.breakpoints.up(360)]: {
		padding: "1.25rem 1rem 0.25rem",
	},
	[theme.breakpoints.up(411)]: {
		padding: "1.25rem 1rem 0.5rem",
	},
	[theme.breakpoints.up(600)]: {
		padding: "1.25rem 1.5rem 0.5rem",
	},
	[theme.breakpoints.up(900)]: {
		padding: "1.25rem 1rem 0.5rem",
	},
}))

const DiscountBody = styled(Box)(({theme}) => ({
	display: "flex",
	flexDirection: "column",
	flex: 1,
	/* top | left and right | bottom */
	padding: "1rem",
	[theme.breakpoints.up(360)]: {
		padding: "1rem 1rem 1.25rem",
	},
	[theme.breakpoints.up(411)]: {
		padding: "1.5rem 1rem 1.25rem",
	},
	[theme.breakpoints.up(600)]: {
		padding: "1.5rem 1.5rem 1.25rem",
	},
	[theme.breakpoints.up(900)]: {
		padding: "1.5rem 1rem 1.5rem",
	},
}))

const DiscountBodyContent = styled(Box)(({theme}) => ({
	flex: 1,
	paddingBottom: "1rem",
	[theme.breakpoints.up(411)]: {
		paddingBottom: "1.5rem",
	},
}))

const FootnoteBox = styled(Box, {
	shouldForwardProp: (prop) => prop !== "showFootnote",
})<{showFootnote: boolean}>(({theme, showFootnote}) => ({
	visibility: showFootnote ? "visible" : "hidden",
	[theme.breakpoints.down(900)]: {
		display: showFootnote ? "block" : "none",
	},
	[theme.breakpoints.up(900)]: {
		display: "none",
	},
}))

export {SettlementOffer}
